import { useQuery } from "@apollo/client";
import {
	Anchor,
	Card,
	Container,
	Group,
	Loader,
	Pagination,
	ScrollArea,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconChartBar, IconSearch } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
	type EmployeeGetActiveUserCountsQuery,
	type EmployeeGetOrganizationsQuery,
	type EmployeeGetUserActivityQuery,
	UserActivityOrderBy,
} from "../../graphql/generated/types.gen";
import {
	EMPLOYEE_GET_ACTIVE_USER_COUNTS,
	EMPLOYEE_GET_ORGANIZATIONS,
	EMPLOYEE_GET_USER_ACTIVITY,
} from "../../graphql/queries";
import { useAuthCompat } from "../../hooks/useAuthCompat";
import { useIsMobile } from "../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/statistics")({
	component: AdminStatisticsPage,
});

const PAGE_SIZE = 20;

const ORDER_BY_OPTIONS: { value: UserActivityOrderBy; label: string }[] = [
	{ value: UserActivityOrderBy.ActiveDays, label: "Active Days" },
	{ value: UserActivityOrderBy.LastActiveAt, label: "Last Active" },
	{ value: UserActivityOrderBy.DisplayName, label: "Name" },
];

function AdminStatisticsPage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();

	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [page, setPage] = useState(1);
	const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
	const [orgSearch, setOrgSearch] = useState("");
	const [debouncedOrgSearch] = useDebouncedValue(orgSearch, 300);
	const [orderBy, setOrderBy] = useState<UserActivityOrderBy | null>(
		UserActivityOrderBy.ActiveDays,
	);

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Reset page when any filter changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, selectedOrgId, orderBy]);

	const { data: countsData } = useQuery<EmployeeGetActiveUserCountsQuery>(
		EMPLOYEE_GET_ACTIVE_USER_COUNTS,
		{
			variables: {
				organizationId: selectedOrgId || undefined,
			},
			skip: !user?.isEmployee,
		},
	);

	const { data: orgData, loading: orgQueryLoading } =
		useQuery<EmployeeGetOrganizationsQuery>(EMPLOYEE_GET_ORGANIZATIONS, {
			variables: {
				search: debouncedOrgSearch || undefined,
				limit: 50,
			},
			skip: !user?.isEmployee,
		});

	const orgLoading = orgQueryLoading || orgSearch !== debouncedOrgSearch;

	const orgOptions = useMemo(
		() =>
			(orgData?.employeeOrganizations.organizations ?? []).map((o) => ({
				value: o.id,
				label: o.name,
			})),
		[orgData],
	);

	const { data, loading } = useQuery<EmployeeGetUserActivityQuery>(
		EMPLOYEE_GET_USER_ACTIVITY,
		{
			variables: {
				organizationId: selectedOrgId || undefined,
				search: debouncedSearch || undefined,
				days: 30,
				orderBy: orderBy ?? undefined,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			},
			skip: !user?.isEmployee,
		},
	);

	if (authLoading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const counts = countsData?.employeeActiveUserCounts;
	const users = data?.employeeUserActivity.users ?? [];
	const totalCount = data?.employeeUserActivity.totalCount ?? 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconChartBar
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>Statistics</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin">&larr; Back to Admin</Anchor>
				</div>

				{counts && (
					<SimpleGrid cols={{ base: 3 }} mb="md">
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								DAU
							</Text>
							<Text size="xl" fw={700}>
								{counts.daily}
							</Text>
						</Card>
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								WAU
							</Text>
							<Text size="xl" fw={700}>
								{counts.weekly}
							</Text>
						</Card>
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								MAU
							</Text>
							<Text size="xl" fw={700}>
								{counts.monthly}
							</Text>
						</Card>
					</SimpleGrid>
				)}

				<SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
					<TextInput
						label="Search"
						placeholder="Search by name or email..."
						leftSection={<IconSearch size={16} />}
						value={search}
						onChange={(e) => setSearch(e.currentTarget.value)}
					/>
					<Select
						label="Filter by organization"
						placeholder="All organizations"
						data={orgOptions}
						value={selectedOrgId}
						onChange={setSelectedOrgId}
						searchable
						onSearchChange={setOrgSearch}
						clearable
						nothingFoundMessage={
							orgLoading ? "Loading..." : "No organizations found"
						}
						rightSection={orgLoading ? <Loader size={16} /> : undefined}
					/>
					<Select
						label="Sort by"
						data={ORDER_BY_OPTIONS}
						value={orderBy}
						onChange={(v) => setOrderBy(v as UserActivityOrderBy | null)}
					/>
				</SimpleGrid>

				{loading ? (
					<div className="flex items-center justify-center py-8">
						<Loader size="lg" />
					</div>
				) : users.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							{debouncedSearch || selectedOrgId
								? "No users found matching filters."
								: "No user activity recorded yet."}
						</Text>
					</Card>
				) : isMobile ? (
					<Stack gap="md">
						{users.map((u) => (
							<Card
								key={u.userId}
								shadow="sm"
								padding="md"
								radius="md"
								withBorder
							>
								<Stack gap="xs">
									<Link
										to="/admin/users/$userId"
										params={{ userId: u.userId }}
										style={{ textDecoration: "none" }}
									>
										<Text fw={600} size="sm">
											{u.displayName}
										</Text>
									</Link>
									<Text size="xs" c="dimmed">
										{u.email}
									</Text>
									<Group justify="space-between">
										<Text size="xs">
											{u.activeDates.length} active day
											{u.activeDates.length !== 1 && "s"}
										</Text>
										<Tooltip
											label={
												u.lastActiveAt
													? new Date(u.lastActiveAt).toLocaleString()
													: "No activity"
											}
										>
											<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
												Last active:{" "}
												{u.lastActiveAt
													? new Date(u.lastActiveAt).toLocaleDateString()
													: "Never"}
											</Text>
										</Tooltip>
									</Group>
								</Stack>
							</Card>
						))}
					</Stack>
				) : (
					<Card shadow="sm" padding={0} radius="md" withBorder>
						<ScrollArea>
							<Table striped highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>User</Table.Th>
										<Table.Th>Email</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>
											Active Days (30d)
										</Table.Th>
										<Table.Th>Last Active</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{users.map((u) => (
										<Table.Tr key={u.userId}>
											<Table.Td>
												<Link
													to="/admin/users/$userId"
													params={{ userId: u.userId }}
													style={{ textDecoration: "none" }}
												>
													<Text size="sm">{u.displayName}</Text>
												</Link>
											</Table.Td>
											<Table.Td>
												<Text size="sm">{u.email}</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm">{u.activeDates.length}</Text>
											</Table.Td>
											<Table.Td>
												<Tooltip
													label={
														u.lastActiveAt
															? new Date(u.lastActiveAt).toLocaleString()
															: "No activity"
													}
												>
													<Text size="sm" style={{ cursor: "default" }}>
														{u.lastActiveAt
															? new Date(u.lastActiveAt).toLocaleDateString()
															: "Never"}
													</Text>
												</Tooltip>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</ScrollArea>
					</Card>
				)}

				{totalPages > 1 && (
					<Group justify="center" mt="md">
						<Pagination
							value={page}
							onChange={setPage}
							total={totalPages}
							size={isMobile ? "sm" : "md"}
						/>
						<Text size="sm" c="dimmed">
							{totalCount} user{totalCount !== 1 && "s"} total
						</Text>
					</Group>
				)}
			</Container>
		</div>
	);
}
